import { createBrowserClient } from "@supabase/ssr";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "./database.types";

// These env variables need to be added to your project and must start with NEXT_PUBLIC_ to be accessible in browser
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || "";
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || "";

// Service role key should only be used on the server side
const supabaseServiceKey = process.env.SUPABASE_SERVICE_ROLE_KEY || "";

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    "Missing Supabase URL or Anonymous Key. Check your environment variables.",
  );
}

// Implement singleton pattern for browser Supabase client
let supabaseInstance: ReturnType<typeof createBrowserClient<Database>> | null =
  null;
let supabaseServiceInstance: ReturnType<typeof createClient<Database>> | null =
  null;

// Create a singleton Supabase client
const getSupabaseClient = () => {
  if (supabaseInstance) return supabaseInstance;

  supabaseInstance = createBrowserClient<Database>(
    supabaseUrl,
    supabaseAnonKey,
    {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: "pkce", // Use PKCE flow for better security in browser environments
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
        // Explicitly set the realtime URL (optional, but can help with connection issues)
        // The client will automatically construct this from supabaseUrl if not provided
      },
      global: {
        headers: {
          "x-application-name": "sell-it-off",
        },
      },
    },
  );

  return supabaseInstance;
};

// Export the singleton instance
export const supabase = getSupabaseClient();

// This function should only be used in server components or API routes
export const createServerSupabaseClient = () => {
  return createClient<Database>(
    process.env.NEXT_PUBLIC_SUPABASE_URL || "",
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || "",
    {
      auth: {
        persistSession: false,
      },
    },
  );
};

// Create a service role client for admin operations (SERVER SIDE ONLY)
// This bypasses RLS policies and should NEVER be used in client-side code
export const getServiceSupabase = () => {
  // Ensure this is only called server-side
  if (typeof window !== "undefined") {
    console.error("Service role client should not be used in client code!");
    return supabase; // Fall back to anon client on client side
  }

  if (supabaseServiceInstance) return supabaseServiceInstance;

  if (!supabaseServiceKey) {
    console.error(
      "Missing Supabase Service Role Key. Check your environment variables.",
    );
    return createServerSupabaseClient(); // Fall back to server client
  }

  supabaseServiceInstance = createClient<Database>(
    supabaseUrl,
    supabaseServiceKey,
    {
      auth: {
        persistSession: false,
      },
    },
  );

  return supabaseServiceInstance;
};
